import { AlertModal, Box, Button, showToast, Stepper, StepperItem, TextBlock } from "@bcc-ui/ui-components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RiskResolvingStep } from "./steps/risk-resolving-step/RiskResolvingStep";
import { SmartGradingStep } from "./steps/smart-grading-step/SmartGradingStep";
import { ValueRecoveryStep } from "./steps/value-recovery-step/ValueRecoveryStep";
import style from "./value-tab.scss";
import ErrorMessage from "components/error-message/ErrorMessage";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import {
    assetGradingService,
    FIRST_STEPPER_NUMBER,
    GradingStep,
    ResolveRiskSummary,
    toStepperNumber,
    WorkflowsForGrading,
} from "services/asset-management/AssetGradingService";
import { AssetSearchRequest, assetService } from "services/asset-management/AssetService";
import { ConflictBehavior, templateService } from "services/workflows/TemplateService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

// TODO BCC-7908 Uncomment for Device Diagnostics step
const VALUE_RECOVERY_STEP_TRANSLATION_KEYS = [
    "AssetManagement.value.steps.smartGrading",
    "AssetManagement.value.steps.resolveRisks",
    //"AssetManagement.value.steps.deviceDiagnostics",
    "AssetManagement.value.steps.valueRecovery",
];

interface Props {
    assetSearchRequest?: AssetSearchRequest;
    onResolveRisks: () => void;
    refreshKey?: number;
}

export interface Step {
    activeStep: number;
    viewedStep: number;
    stepName?: GradingStep;
}

// TODO BCC-7908 Uncomment for Device Diagnostics step
/*function isGradingCompleted(step?: GradingStep): boolean {
    return step === GradingStep.DEVICE_DIAGNOSED || step === GradingStep.VALUE_RECOVERY;
}*/

export const ValueTab = (props: Props) => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    const [isSmartGradingEnabled, setIsSmartGradingEnabled] = useState(false);
    const [gradableAssetCount, setGradableAssetCount] = useState<number>();
    const [riskSummaryData, setRiskSummaryData] = useState<ResolveRiskSummary>({
        assetsExcludingLockedAndQuarantinedCount: 0,
        lockedAssetsCount: 0,
        quarantinedAssetsCounts: 0,
    });
    const [isGradingProgressLoaded, setIsGradingProgressLoaded] = useState<boolean>(false);
    const [isWorkflowsLoaded, setIsWorkflowsLoaded] = useState<boolean>(false);
    const [isLaunchingGrading, setIsLaunchingGrading] = useState<boolean>(false);
    const [isGradingErrorDialogOpen, setIsGradingErrorDialogOpen] = useState<boolean>(false);
    const [isSaveErrorDialogOpen, setIsSaveErrorDialogOpen] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [stepState, setStepState] = useState<Step>({
        activeStep: FIRST_STEPPER_NUMBER,
        viewedStep: FIRST_STEPPER_NUMBER,
    });
    const [templateSelected, setTemplateSelected] = React.useState<boolean>(false);
    const [workflowUuid, setWorkflowUuid] = React.useState<string | undefined>(undefined);
    const [workflowsForGrading, setWorkflowsForGrading] = useState<WorkflowsForGrading>({
        templates: [],
        workflows: [],
    });
    // TODO BCC-7908 Uncomment for Device Diagnostics step
    // const [totalAssetsToGrade, setTotalAssetsToGrade] = useState<number>();

    useEffect(() => {
        const abortController = new AbortController();
        if (!props.assetSearchRequest || !props.assetSearchRequest?.groupUuid) {
            return;
        }
        assetGradingService
            .fetchGradingStepProgress(props.assetSearchRequest?.groupUuid, abortController)
            .then((progress) => {
                if (progress !== undefined) {
                    setIsSmartGradingEnabled(progress.enabled);
                    const stepperNumber = toStepperNumber(progress.step);
                    setStepState({ activeStep: stepperNumber, viewedStep: stepperNumber, stepName: progress.step });
                    if (progress.workflowUuid) {
                        setWorkflowUuid(progress.workflowUuid);
                    }
                }
                setIsGradingProgressLoaded(true);
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    const response = JSON.parse(error.message);
                    if (response.error.code === "NOT_FOUND") {
                        setStepState({ activeStep: FIRST_STEPPER_NUMBER, viewedStep: FIRST_STEPPER_NUMBER });
                    } else {
                        logger.error("Failed to fetch grading step progress: ", error);
                        setIsError(true);
                    }
                }
            })
            .finally(() => {
                setIsGradingProgressLoaded(true);
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest?.groupUuid, props.refreshKey]);

    useEffect(() => {
        if (!isSmartGradingEnabled) {
            return;
        }

        const abortController = new AbortController();

        assetService
            .fetchAssetOverview(props.assetSearchRequest ?? { paths: [] }, abortController)
            .then((assetOverview) => setGradableAssetCount(assetOverview.totalAssets))
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch asset overview for smart grading: ", error);
                    setIsError(true);
                }
            });
        return () => abortController.abort();
    }, [isSmartGradingEnabled, props.refreshKey]);

    useEffect(() => {
        if (!props.assetSearchRequest) {
            return;
        }
        const abortController = new AbortController();
        assetGradingService
            .fetchResolveRiskSummary(props.assetSearchRequest, abortController)
            .then((riskSummary) => {
                setRiskSummaryData(riskSummary);
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch risk summary for risk resolving: ", error);
                    setIsError(true);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest]);

    useEffect(() => {
        const abortController = new AbortController();
        assetGradingService
            .fetchWorkflowsForGrading(abortController)
            .then((response) => {
                setWorkflowsForGrading(response);
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch workflows and templates for grading: ", error);
                    setIsError(true);
                }
            })
            .finally(() => setIsWorkflowsLoaded(true));
        return () => abortController.abort();
    }, []);

    const saveGradingStep = (step: GradingStep, enabled: boolean, isRerun?: boolean) => {
        const groupUuid = props.assetSearchRequest?.groupUuid;
        if (!groupUuid) {
            return;
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        assetGradingService
            .saveGradingStep(groupUuid, step, enabled, workflowUuid, abortController, isRerun)
            .catch((error: Error) => {
                logger.error(`Failed to save grading step ${step}: `, error);
                setIsSaveErrorDialogOpen(true);
            });
    };

    const moveToGradingStep = (step: GradingStep, isRerun?: boolean) => {
        const stepperNumber = toStepperNumber(step);
        setStepState({ activeStep: stepperNumber, viewedStep: stepperNumber, stepName: step });
        saveGradingStep(step, true, isRerun);
    };

    const performLaunchGrading = (
        workflowUuidParam: string,
        templateSelectedParam: boolean,
        isRerun: boolean = false
    ) => {
        const groupUuid = props.assetSearchRequest?.groupUuid;
        if (!groupUuid || !workflowUuidParam) {
            return;
        }
        setIsLaunchingGrading(true);

        const launchGradingWithWorkflow = (workflowToLaunch: string) =>
            assetGradingService
                .launchGrading(workflowToLaunch, props.assetSearchRequest ?? { paths: [] })
                .then((response) => {
                    const toastMessageKey = isRerun
                        ? "AssetManagement.value.smartGrading.rerun.launched"
                        : "AssetManagement.value.smartGrading.stepTwo.gradingLaunched";
                    showToast(
                        t(toastMessageKey, {
                            count: response.assetsToGradeCount,
                        }),
                        "success"
                    );
                    // TODO BCC-7908 Uncomment for Device Diagnostics step
                    //setTotalAssetsToGrade(response.assetsToGradeCount);
                    moveToGradingStep(GradingStep.VALUE_RECOVERY, isRerun);
                })
                .catch((error: Error) => {
                    logger.error("Failed to launch grading: ", error);
                    setIsGradingErrorDialogOpen(true);
                });

        if (templateSelectedParam) {
            const selectedTemplate = workflowsForGrading.templates.find((each) => each.uuid === workflowUuidParam);
            if (selectedTemplate && selectedTemplate.workflowUuid) {
                launchGradingWithWorkflow(selectedTemplate.workflowUuid).finally(() => setIsLaunchingGrading(false));
            } else {
                templateService
                    .save(workflowUuidParam, ConflictBehavior.OVERWRITE)
                    .then((response) => launchGradingWithWorkflow(response.workflowUuid))
                    .catch((error: Error) => {
                        logger.error("Failed to save grading template as a workflow: ", error);
                        setIsGradingErrorDialogOpen(true);
                    })
                    .finally(() => setIsLaunchingGrading(false));
            }
        } else {
            launchGradingWithWorkflow(workflowUuidParam).finally(() => setIsLaunchingGrading(false));
        }
    };

    const launchGrading = () => {
        if (!workflowUuid) {
            return;
        }
        performLaunchGrading(workflowUuid, templateSelected, false);
    };

    const handleRerunGrading = (workflowUuidParam: string, templateSelectedParam: boolean) => {
        performLaunchGrading(workflowUuidParam, templateSelectedParam, true);
    };

    const handleSmartGradingEnabled = (enabled: boolean) => {
        setIsSmartGradingEnabled(enabled);
        if (stepState.activeStep > 1 && stepState.stepName) {
            saveGradingStep(stepState.stepName, enabled);
        }
    };

    useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const renderStepCard = () => {
        switch (stepState.viewedStep) {
            case 1:
                return (
                    <SmartGradingStep
                        gradableAssetCount={gradableAssetCount}
                        isSmartGradingEnabled={isSmartGradingEnabled}
                        setIsSmartGradingEnabled={setIsSmartGradingEnabled}
                        onStepChange={setStepState}
                        onSmartGradingChange={handleSmartGradingEnabled}
                        setWorkflowUuid={setWorkflowUuid}
                        setTemplateSelected={setTemplateSelected}
                        workflows={workflowsForGrading.workflows}
                        templates={workflowsForGrading.templates}
                        workflowUuid={workflowUuid}
                        currentlySavedStep={stepState.activeStep}
                    />
                );
            case 2:
                return (
                    <RiskResolvingStep
                        isSmartGradingEnabled={isSmartGradingEnabled}
                        handleSmartGradingChange={setIsSmartGradingEnabled}
                        handleGradingStepSave={saveGradingStep}
                        handleResolveRisks={props.onResolveRisks}
                        handleContinueGrading={launchGrading}
                        isLaunchingGrading={isLaunchingGrading}
                        allAssetsEligibleForGrading={
                            riskSummaryData.lockedAssetsCount === 0 && riskSummaryData.quarantinedAssetsCounts === 0
                        }
                        someAssetsEligibleForGrading={riskSummaryData.assetsExcludingLockedAndQuarantinedCount > 0}
                        data={riskSummaryData}
                        currentlyCompletedStep={stepState.activeStep}
                    />
                );
            // TODO BCC-7908 Uncomment and add step back
            // case 3:
            //     return (
            //         <DeviceDiagnosticsStep
            //             totalAssetsToGrade={
            //                 totalAssetsToGrade ?? riskSummaryData.assetsExcludingLockedAndQuarantinedCount
            //             }
            //             isGradingCompleted={isGradingCompleted(stepState.stepName)}
            //             isSmartGradingEnabled={isSmartGradingEnabled}
            //             eligibleAssetsCount={riskSummaryData.assetsExcludingLockedAndQuarantinedCount}
            //             handleSmartGradingChange={setIsSmartGradingEnabled}
            //             handleGradingStepSave={saveGradingStep}
            //             onDiagnosticsCompleted={() => moveToGradingStep(GradingStep.VALUE_RECOVERY)}
            //             currentlyCompletedStep={stepState.activeStep}
            //         />
            //     );
            case 3:
                return (
                    <ValueRecoveryStep
                        assetSearchRequest={props.assetSearchRequest}
                        onResolveAssetRisks={props.onResolveRisks}
                        isSmartGradingEnabled={isSmartGradingEnabled}
                        eligibleAssetsCount={riskSummaryData.assetsExcludingLockedAndQuarantinedCount}
                        handleSmartGradingChange={setIsSmartGradingEnabled}
                        handleGradingStepSave={saveGradingStep}
                        isLaunchingGrading={isLaunchingGrading}
                        workflows={workflowsForGrading.workflows}
                        templates={workflowsForGrading.templates}
                        workflowUuid={workflowUuid}
                        onRerunGrading={handleRerunGrading}
                    />
                );
            default:
                return null;
        }
    };

    if (isError) {
        return <ErrorMessage />;
    }

    return (
        <>
            <Box columnLayout gap="Md" testId={testIds.workArea.asset.customView.value.itself}>
                <Stepper>
                    {VALUE_RECOVERY_STEP_TRANSLATION_KEYS.map((step, index) => {
                        const trackableNumber = index + 1;
                        return (
                            <StepperItem
                                key={index}
                                stepNumber={trackableNumber}
                                isActive={
                                    trackableNumber >= stepState.viewedStep && trackableNumber <= stepState.activeStep
                                }
                                isCompleted={trackableNumber < stepState.viewedStep}
                                onClick={() => {
                                    if (trackableNumber <= stepState.activeStep) {
                                        setStepState((prev) => ({ ...prev, viewedStep: trackableNumber }));
                                    }
                                }}
                            >
                                {t(step)}
                            </StepperItem>
                        );
                    })}
                </Stepper>
                {!isGradingProgressLoaded || !isWorkflowsLoaded ? (
                    <LoadingIndicator className={style.loaderPosition} />
                ) : (
                    renderStepCard()
                )}
            </Box>
            <AlertModal
                title={t("AssetManagement.value.smartGrading.stepTwo.gradingLaunchFailedTitle")}
                variant="Error"
                isOpen={isGradingErrorDialogOpen}
                hideModal={() => setIsGradingErrorDialogOpen(false)}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setIsGradingErrorDialogOpen(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <TextBlock>{t("AssetManagement.value.smartGrading.stepTwo.gradingLaunchFailed")}</TextBlock>
            </AlertModal>
            <AlertModal
                title={t("Common.resultErrorTitle")}
                variant="Error"
                isOpen={isSaveErrorDialogOpen}
                hideModal={() => setIsSaveErrorDialogOpen(false)}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setIsSaveErrorDialogOpen(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <TextBlock>{t("Common.resultErrorMessage")}</TextBlock>
            </AlertModal>
        </>
    );
};
